import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CheckIcon,
  ClockIcon,
  DocumentTextIcon,
  InfoIcon,
  UserPlusIcon,
  WarningIcon,
  XIcon,
} from '../../components/ui';
import { formatDateTime } from '../../config/appConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import type {
  CrewFileAuditAction,
  CrewHistoryRow,
  CrewReplacementAuditRow,
} from './types';

export function auditActionLabel(action: CrewReplacementAuditRow['action']): string {
  switch (action) {
    case 'ASSIGNED':
      return 'Assigned';
    case 'CLEARED':
      return 'Cleared';
    case 'STATUS_CHANGED':
      return 'Status';
    case 'COMPLETED':
      return 'Completed';
    case 'CANCELLED':
      return 'Cancelled';
    case 'SEEDED':
      return 'Seeded';
    case 'CREATED':
      return 'Created';
    case 'UPDATED':
      return 'Updated';
    default:
      return action;
  }
}

function fileActionLabel(action: CrewFileAuditAction): string {
  switch (action) {
    case 'CREATED':
      return 'Created';
    case 'UPDATED':
      return 'Updated';
    case 'DELETED':
      return 'Deleted';
    case 'DOCUMENT_UPLOADED':
      return 'Document uploaded';
    case 'DOCUMENT_DELETED':
      return 'Document deleted';
    default:
      return action;
  }
}

type FlowTone = 'when' | 'if' | 'for' | 'then' | 'done' | 'cancel' | 'file';

function replacementFlowMeta(action: CrewReplacementAuditRow['action']): {
  tone: FlowTone;
  prefix: string;
  label: string;
} {
  switch (action) {
    case 'CREATED':
    case 'SEEDED':
      return { tone: 'when', prefix: 'When', label: auditActionLabel(action) };
    case 'STATUS_CHANGED':
    case 'CLEARED':
      return { tone: 'if', prefix: 'If', label: auditActionLabel(action) };
    case 'ASSIGNED':
    case 'UPDATED':
      return { tone: 'then', prefix: 'Then', label: auditActionLabel(action) };
    case 'COMPLETED':
      return { tone: 'done', prefix: 'Then', label: auditActionLabel(action) };
    case 'CANCELLED':
      return { tone: 'cancel', prefix: 'Then', label: auditActionLabel(action) };
    default:
      return { tone: 'then', prefix: 'Then', label: auditActionLabel(action) };
  }
}

function fileFlowMeta(action: CrewFileAuditAction): {
  tone: FlowTone;
  prefix: string;
  label: string;
} {
  switch (action) {
    case 'CREATED':
      return { tone: 'when', prefix: 'When', label: fileActionLabel(action) };
    case 'DELETED':
      return { tone: 'cancel', prefix: 'Then', label: fileActionLabel(action) };
    case 'DOCUMENT_UPLOADED':
    case 'DOCUMENT_DELETED':
      return { tone: 'file', prefix: 'Then', label: fileActionLabel(action) };
    case 'UPDATED':
    default:
      return { tone: 'file', prefix: 'Then', label: fileActionLabel(action) };
  }
}

function toneClasses(tone: FlowTone, isDark: boolean): { ring: string; icon: string } {
  switch (tone) {
    case 'when':
      return {
        ring: isDark ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
        icon: isDark ? 'text-emerald-300' : 'text-emerald-700',
      };
    case 'if':
      return {
        ring: isDark ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-800',
        icon: isDark ? 'text-amber-300' : 'text-amber-800',
      };
    case 'for':
      return {
        ring: isDark ? 'bg-violet-500/20 text-violet-300' : 'bg-violet-100 text-violet-800',
        icon: isDark ? 'text-violet-300' : 'text-violet-800',
      };
    case 'then':
      return {
        ring: isDark ? 'bg-sky-500/20 text-sky-300' : 'bg-sky-100 text-sky-800',
        icon: isDark ? 'text-sky-300' : 'text-sky-800',
      };
    case 'done':
      return {
        ring: isDark ? 'bg-emerald-500/25 text-emerald-200' : 'bg-emerald-200/80 text-emerald-900',
        icon: isDark ? 'text-emerald-200' : 'text-emerald-900',
      };
    case 'cancel':
      return {
        ring: isDark ? 'bg-zinc-500/20 text-zinc-300' : 'bg-zinc-200 text-zinc-700',
        icon: isDark ? 'text-zinc-300' : 'text-zinc-700',
      };
    case 'file':
      return {
        ring: isDark ? 'bg-indigo-500/20 text-indigo-300' : 'bg-indigo-100 text-indigo-800',
        icon: isDark ? 'text-indigo-300' : 'text-indigo-800',
      };
  }
}

function FlowIcon({ tone, className }: { tone: FlowTone; className?: string }) {
  const cls = cn('h-4 w-4', className);
  switch (tone) {
    case 'when':
      return <ClockIcon className={cls} />;
    case 'if':
      return <WarningIcon className={cls} />;
    case 'for':
      return <InfoIcon className={cls} />;
    case 'then':
      return <UserPlusIcon className={cls} />;
    case 'done':
      return <CheckIcon className={cls} />;
    case 'cancel':
      return <XIcon className={cls} />;
    case 'file':
      return <DocumentTextIcon className={cls} />;
  }
}

function roleLabel(role?: CrewReplacementAuditRow['role']): string | null {
  if (role === 'outgoing') return 'Outgoing';
  if (role === 'incoming') return 'Incoming / relief';
  if (role === 'related') return 'Possible';
  return null;
}

type ReplacementGroup = {
  replacementId: number;
  vessel?: string;
  vesselId?: number;
  rank?: string;
  status?: string;
  outgoingCrewName?: string;
  incomingCrewName?: string;
  role?: CrewReplacementAuditRow['role'];
  events: CrewReplacementAuditRow[];
};

function groupByReplacement(rows: CrewReplacementAuditRow[]): ReplacementGroup[] {
  const map = new Map<number, ReplacementGroup>();
  for (const row of rows) {
    let g = map.get(row.replacementId);
    if (!g) {
      g = groupFromEvents([row]);
      map.set(row.replacementId, g);
    } else {
      if (!g.vessel && row.vessel) g.vessel = row.vessel;
      if (!g.vesselId && row.vesselId) g.vesselId = row.vesselId;
      if (!g.rank && row.rank) g.rank = row.rank;
      if (!g.status && row.status) g.status = row.status;
      if (!g.outgoingCrewName && row.outgoingCrewName) g.outgoingCrewName = row.outgoingCrewName;
      if (!g.incomingCrewName && row.incomingCrewName) g.incomingCrewName = row.incomingCrewName;
      if (!g.role && row.role) g.role = row.role;
      g.events.push(row);
    }
  }

  const groups = Array.from(map.values());
  for (const g of groups) {
    g.events.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  }
  groups.sort((a, b) => {
    const aLast = a.events[a.events.length - 1]?.createdAt ?? '';
    const bLast = b.events[b.events.length - 1]?.createdAt ?? '';
    return bLast.localeCompare(aLast);
  });
  return groups;
}

function groupFromEvents(events: CrewReplacementAuditRow[]): ReplacementGroup {
  const first = events[0];
  return {
    replacementId: first?.replacementId ?? 0,
    vessel: first?.vessel,
    vesselId: first?.vesselId,
    rank: first?.rank,
    status: first?.status,
    outgoingCrewName: first?.outgoingCrewName,
    incomingCrewName: first?.incomingCrewName,
    role: first?.role,
    events: [...events],
  };
}

const FlowNode: React.FC<{
  tone: FlowTone;
  title: string;
  detail?: string | null;
  meta?: string | null;
  isLast?: boolean;
  showConnector?: boolean;
}> = ({ tone, title, detail, meta, isLast, showConnector = true }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const toneCls = toneClasses(tone, isDark);

  return (
    <div className="relative flex gap-3">
      <div className="flex w-8 shrink-0 flex-col items-center">
        <span
          className={cn(
            'relative z-[1] inline-flex h-8 w-8 items-center justify-center rounded-full',
            toneCls.ring,
          )}
        >
          <FlowIcon tone={tone} className={toneCls.icon} />
        </span>
        {showConnector && !isLast ? (
          <span
            className={cn(
              'mt-1 w-px flex-1 min-h-[1.25rem]',
              isDark ? 'bg-zinc-600' : 'bg-slate-300',
            )}
            aria-hidden
          />
        ) : null}
      </div>
      <div className={cn('min-w-0 flex-1 pb-5', isLast && 'pb-1')}>
        <div className={cn('text-sm font-semibold leading-snug', colors.text)}>{title}</div>
        {detail ? (
          <div className={cn('mt-0.5 text-sm leading-snug', colors.textSecondary)}>{detail}</div>
        ) : null}
        {meta ? <div className={cn('mt-1 text-xs', colors.textMuted)}>{meta}</div> : null}
      </div>
    </div>
  );
};

function asHistoryRows(rows: CrewHistoryRow[] | CrewReplacementAuditRow[]): CrewHistoryRow[] {
  if (!rows.length) return [];
  const first = rows[0] as CrewHistoryRow | CrewReplacementAuditRow;
  if ('source' in first) return rows as CrewHistoryRow[];
  return (rows as CrewReplacementAuditRow[]).map((r) => ({ ...r, source: 'replacement' as const }));
}

export type HistoryFilter = 'all' | 'file' | 'replacement';

/** Replacement-only timeline (planner modal). */
export const ReplacementAuditFlow: React.FC<{
  rows: CrewReplacementAuditRow[];
  groupBySlot?: boolean;
  emptyMessage?: string;
}> = ({ rows, groupBySlot = false, emptyMessage }) => (
  <CrewHistoryFlow
    rows={rows.map((r) => ({ ...r, source: 'replacement' as const }))}
    groupReplacements={groupBySlot}
    emptyMessage={emptyMessage}
    showFilters={false}
  />
);

export const CrewHistoryFlow: React.FC<{
  rows: CrewHistoryRow[] | CrewReplacementAuditRow[];
  groupReplacements?: boolean;
  emptyMessage?: string;
  showFilters?: boolean;
}> = ({
  rows: rawRows,
  groupReplacements = true,
  emptyMessage = 'No history yet.',
  showFilters = true,
}) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [filter, setFilter] = useState<HistoryFilter>('all');
  const rows = useMemo(() => asHistoryRows(rawRows), [rawRows]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    return rows.filter((r) => r.source === filter);
  }, [rows, filter]);

  const fileEvents = useMemo(
    () =>
      filtered
        .filter((r): r is Extract<CrewHistoryRow, { source: 'file' }> => r.source === 'file')
        .sort((a, b) => a.createdAt.localeCompare(b.createdAt)),
    [filtered],
  );

  const replacementGroups = useMemo(() => {
    const repl = filtered.filter(
      (r): r is Extract<CrewHistoryRow, { source: 'replacement' }> => r.source === 'replacement',
    );
    if (!groupReplacements) {
      const events = [...repl].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return events.length ? [groupFromEvents(events)] : [];
    }
    return groupByReplacement(repl);
  }, [filtered, groupReplacements]);

  // Interleave: build a chronological list of "blocks" by latest activity
  const blocks = useMemo(() => {
    type Block =
      | { kind: 'file'; row: Extract<CrewHistoryRow, { source: 'file' }> }
      | { kind: 'replacement'; group: ReplacementGroup };

    const out: Block[] = [];
    if (!groupReplacements && filter !== 'file') {
      // Flat mixed timeline for slot-only planner
      const flat = [...filtered].sort((a, b) => a.createdAt.localeCompare(b.createdAt));
      return flat.map((row): Block =>
        row.source === 'file'
          ? { kind: 'file', row }
          : { kind: 'replacement', group: groupFromEvents([row]) },
      );
    }

    for (const row of fileEvents) {
      out.push({ kind: 'file', row });
    }
    for (const group of replacementGroups) {
      out.push({ kind: 'replacement', group });
    }
    out.sort((a, b) => {
      const aAt =
        a.kind === 'file'
          ? a.row.createdAt
          : a.group.events[a.group.events.length - 1]?.createdAt ?? '';
      const bAt =
        b.kind === 'file'
          ? b.row.createdAt
          : b.group.events[b.group.events.length - 1]?.createdAt ?? '';
      return bAt.localeCompare(aAt);
    });
    return out;
  }, [fileEvents, replacementGroups, filtered, groupReplacements, filter]);

  const filters: Array<{ id: HistoryFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'file', label: 'File' },
    { id: 'replacement', label: 'Replacements' },
  ];

  return (
    <div className="space-y-4">
      {showFilters ? (
        <div className="flex flex-wrap gap-1.5">
          {filters.map((f) => {
            const active = filter === f.id;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  'rounded-md px-2.5 py-1 text-xs font-medium transition-colors',
                  active
                    ? isDark
                      ? 'bg-sky-500/25 text-sky-200'
                      : 'bg-sky-100 text-sky-900'
                    : isDark
                      ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200',
                )}
              >
                {f.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {!filtered.length ? (
        <p className={cn('text-sm', colors.textSecondary)}>{emptyMessage}</p>
      ) : (
        <div className="space-y-6">
          {blocks.map((block, blockIdx) => {
            if (block.kind === 'file') {
              const row = block.row;
              const meta = fileFlowMeta(row.action);
              const isLast = blockIdx === blocks.length - 1;
              return (
                <FlowNode
                  key={`file-${row.id}`}
                  tone={meta.tone}
                  title={`${meta.prefix}: ${meta.label}`}
                  detail={row.summary}
                  meta={[formatDateTime(row.createdAt), row.userName].filter(Boolean).join(' · ')}
                  isLast={isLast}
                />
              );
            }

            const group = block.group;
            const role = roleLabel(group.role);
            const branchDetail = [
              group.outgoingCrewName ? `Outgoing ${group.outgoingCrewName}` : null,
              group.incomingCrewName ? `Relief ${group.incomingCrewName}` : null,
              role,
              group.status,
            ]
              .filter(Boolean)
              .join(' · ');
            const branchTitle = [
              'For:',
              group.vessel || 'Replacement',
              group.rank ? `(${group.rank})` : null,
            ]
              .filter(Boolean)
              .join(' ');
            const showBranch = groupReplacements && !!group.vessel;
            const eventCount = group.events.length;
            const isLastBlock = blockIdx === blocks.length - 1;

            return (
              <div key={`repl-${group.replacementId}-${blockIdx}`} className="min-w-0">
                {showBranch ? (
                  <FlowNode
                    tone="for"
                    title={branchTitle}
                    detail={branchDetail || null}
                    isLast={false}
                    showConnector
                  />
                ) : null}

                <div className={cn(showBranch && 'ml-4 pl-2')}>
                  {showBranch ? (
                    <div
                      className={cn(
                        'mb-1 ml-3 border-l pl-5',
                        isDark ? 'border-zinc-600' : 'border-slate-300',
                      )}
                    >
                      {group.events.map((row, idx) => {
                        const meta = replacementFlowMeta(row.action);
                        const statusBit =
                          row.fromStatus || row.toStatus
                            ? [row.fromStatus, row.toStatus].filter(Boolean).join(' → ')
                            : null;
                        return (
                          <FlowNode
                            key={row.id}
                            tone={meta.tone}
                            title={`${meta.prefix}: ${meta.label}`}
                            detail={[row.summary, statusBit].filter(Boolean).join(' · ')}
                            meta={[formatDateTime(row.createdAt), row.userName]
                              .filter(Boolean)
                              .join(' · ')}
                            isLast={idx === eventCount - 1 && isLastBlock}
                          />
                        );
                      })}
                    </div>
                  ) : (
                    group.events.map((row, idx) => {
                      const meta = replacementFlowMeta(row.action);
                      const statusBit =
                        row.fromStatus || row.toStatus
                          ? [row.fromStatus, row.toStatus].filter(Boolean).join(' → ')
                          : null;
                      return (
                        <FlowNode
                          key={row.id}
                          tone={meta.tone}
                          title={`${meta.prefix}: ${meta.label}`}
                          detail={[row.summary, statusBit].filter(Boolean).join(' · ')}
                          meta={[formatDateTime(row.createdAt), row.userName]
                            .filter(Boolean)
                            .join(' · ')}
                          isLast={idx === eventCount - 1 && isLastBlock}
                        />
                      );
                    })
                  )}
                </div>

                {showBranch && group.vesselId ? (
                  <div className="ml-11 mt-[-0.25rem]">
                    <Link
                      to={`/crew/replacement-planner?vesselId=${group.vesselId}`}
                      className={cn('text-xs hover:underline', colors.primaryFg)}
                    >
                      View on planner
                    </Link>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReplacementAuditFlow;
